import React from 'react';
import { connect } from 'react-redux';
import { reportCustomError } from '@utils/owlReporter';
import DropMenu from '@roo/roo/DropMenu';
import Input from '@roo/roo/Input';
import Icon from '@roo/roo/Icon';
import { trimAllSpace } from '@utiljs/string';
import { selectCurrentPoi } from '@utils/poiHelper';
import { setCookie } from '@utiljs/cookie';
import { db } from '@utils/wmIndexDB';
import * as LX from '@utils/lxWrapper';
import * as Ids from '@utils/ids';
import { AjaxType } from '@projects/Shell/store/models/common';
import { useLiveQuery } from 'dexie-react-hooks';
import { debounce } from 'lodash';
import { RootState } from '../../../../store';
import styles from './PoiDropMenu.module.scss';
import KangarooLog from './KangarooLogo';
import { IAllPoiList, CustomizedShowArrType } from '../../../../api/define';
import pageJump from '../../../../utils/pageJump';

interface PoiDropMenuProps {
    wmPoiName: string;
    /** 自定义配置 */
    customizedShowArr: CustomizedShowArrType[];
    poilistAjaxType: AjaxType;
}

const { useState, useEffect, useRef, useCallback } = React;

/** 门店下拉菜单 */
const PoiDropMenu: React.FC<PoiDropMenuProps> = ({
    wmPoiName,
    customizedShowArr,
    poilistAjaxType,
}) => {
    /** 门店搜索keyword */
    const [searchVal, setSearchVal] = useState('');
    /** 菜单展开状态 */
    const [menuVisible, setMenuVisible] = useState(false);
    /** 门店列表 */
    const [poiList, setPoiList] = useState<IAllPoiList[]>([]);

    const [firstQueryDone, setFirstQueryDone] = useState(false);

    // 添加一个标记，表示是否在中文输入中
    const [isComposing, setIsComposing] = useState(false);

    const latestSearchValRef = useRef<string>('');

    /** indexedDB中门店数据 */
    const poiListIdLength = useLiveQuery(async () => {
        let count;
        try {
            count = await db.poiList.count();
        } catch (e) {
            if (poilistAjaxType !== AjaxType.PENDING) {
                reportCustomError({
                    name: 'db获取poiList异常',
                    message: `db获取poiListIdLength异常-${e}`,
                });
            }
        }
        return count || 0;
    });
    const poiNameCache = useRef(new Map<number, string>());

    // 将搜索逻辑抽离出来并使用 debounce
    const performSearch = useCallback(
        debounce(async (searchValue: string) => {
            const searchValTrim = trimAllSpace(searchValue).toLowerCase();
            let matchCount = 0;
            try {
                db.poiList
                    .filter(poi => {
                        if (matchCount >= 20) return false;

                        const { id, poiName } = poi;
                        const idStr = id.toString();
                        if (searchValTrim === '') {
                            return true;
                        }

                        // 先检查 ID 匹配，这个开销较小
                        if (idStr.indexOf(searchValTrim) > -1) {
                            matchCount += 1;
                            return true;
                        }

                        // 从缓存获取小写名称
                        let poiNameLower = poiNameCache.current.get(id);
                        if (!poiNameLower) {
                            poiNameLower = poiName.toString().toLowerCase();
                            poiNameCache.current.set(id, poiNameLower);
                        }

                        if (poiNameLower.indexOf(searchValTrim) > -1) {
                            matchCount += 1;
                            return true;
                        }

                        return false;
                    })
                    .limit(20)
                    .toArray()
                    .then(res => {
                        if (poiListIdLength !== undefined && poiListIdLength > 0) {
                            setFirstQueryDone(true);
                        }
                        // 确保此次搜索结果与当前最新的搜索关键字一致
                        if (latestSearchValRef.current === searchValue) {
                            setPoiList(res);
                        }
                    })
                    .catch(e => {
                        if (poilistAjaxType !== AjaxType.PENDING) {
                            reportCustomError({
                                name: 'db获取poiList异常',
                                message: `db获取poiList异常-dbcatch-${e}`,
                            });
                        }
                        if (poiListIdLength !== undefined && poiListIdLength > 0) {
                            setFirstQueryDone(true);
                        }
                    });
            } catch (e) {
                if (poilistAjaxType !== AjaxType.PENDING) {
                    reportCustomError({
                        name: 'db获取poiList异常',
                        message: `db获取poiList异常-${e}`,
                    });
                }
                if (poiListIdLength !== undefined && poiListIdLength > 0) {
                    setFirstQueryDone(true);
                }
            }
        }, 300), // 设置较短的延迟时间，保持响应的流畅性
        [poiListIdLength, poilistAjaxType], // 依赖项
    );

    // 监听搜索值变化
    useEffect(() => {
        latestSearchValRef.current = searchVal;
        performSearch(searchVal);
    }, [searchVal, performSearch]);

    /** 门店列表DropMenu */
    const renderPoiListDrop = () => {
        return (
            <div
                className={styles.menuContainer}
                onMouseLeave={e => {
                    if (isComposing) {
                        e.stopPropagation(); // 当打开了中文输入法时，不触发onMouseLeave（不关闭dropdown）
                    }
                }}
            >
                {/* 门店搜索 */}
                <div className={`${styles.inputWrapper} global-input-wrapper`}>
                    <Input
                        placeholder="输入门店名称或门店ID即可快速查询"
                        value={searchVal}
                        onChange={e => setSearchVal(e.target.value)}
                        onCompositionStart={() => {
                            setIsComposing(true); // 开始中文输入法
                        }}
                        onCompositionEnd={() => {
                            setIsComposing(false); // 结束中文输入法
                        }}
                    />
                </div>

                {/* 门店列表 */}
                <ul className={styles.poiList}>
                    {poiList.length > 0 ? (
                        poiList.map(poi => (
                            <li
                                key={poi.id}
                                className={
                                    poi.businessDesc !== '营业中' && poi.id !== -1
                                        ? styles.noOpen
                                        : ''
                                }
                                onClick={() => {
                                    LX.reportMC('b_waimai_e_t733qe4c_mc', 'c_waimai_e_fa4ps4ng', {
                                        acctId: Ids.getAcctId(),
                                    });
                                    selectCurrentPoi(`${poi.id}`, poi.poiName, '', poi.status);
                                }}
                            >
                                {poi.kangarooPackageInfo ? (
                                    <div className={styles.logo}>
                                        <KangarooLog
                                            status={poi.kangarooPackageInfo.status}
                                            version={poi.kangarooPackageInfo.version}
                                        />
                                    </div>
                                ) : (
                                    ''
                                )}
                                <div className={styles.content} title={poi.poiName}>
                                    {poi.poiName}
                                </div>
                                <div className={styles.tag}>
                                    {poi.status ? poi.businessDesc : '待设置'}
                                </div>
                            </li>
                        ))
                    ) : (
                        <li>没有搜到相关的门店</li>
                    )}
                </ul>

                <hr />

                {/* 查看全部门店 */}
                <div
                    className={styles.toAll}
                    onClick={() => {
                        setCookie('wmPoiId', '-1');
                        pageJump.go('/v2/shop/manage');
                        window.location.reload();
                    }}
                >
                    {customizedShowArr.includes(CustomizedShowArrType.WM_POI_ID_ALL) ? null : (
                        <>
                            查看全部门店 <Icon name="chevron-right" size={14} />
                        </>
                    )}
                </div>
            </div>
        );
    };

    return (
        <DropMenu
            placement="bottom"
            menuComponent={renderPoiListDrop()}
            trigger="hover"
            flip={false}
            onVisibleChange={status => {
                if (status) {
                    LX.reportMV('b_waimai_e_t733qe4c_mv', 'c_waimai_e_fa4ps4ng', {
                        acctId: Ids.getAcctId(),
                    });
                    const searchValTrim = trimAllSpace(searchVal);
                    /** 门店列表展示异常上报 */
                    if (
                        !searchValTrim &&
                        poilistAjaxType !== AjaxType.PENDING &&
                        (!poiList || !poiList.length) &&
                        firstQueryDone
                    ) {
                        reportCustomError({
                            name: '门店列表展示异常',
                            message: '门店列表展示异常',
                            category:
                                poilistAjaxType === AjaxType.RESOVLE
                                    ? 'WHITE_PAGE_ERROR'
                                    : 'jsError',
                            level: poilistAjaxType === AjaxType.RESOVLE ? 'ERROR' : 'WARN',
                        });
                    }
                }
                setMenuVisible(status);
            }}
        >
            <div className={styles.currentPoi} title={wmPoiName}>
                <span className={styles.txt}>{wmPoiName}</span>
                <Icon name={menuVisible ? 'chevron-up' : 'chevron-down'} size={12} />
            </div>
        </DropMenu>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        wmPoiName: state.shellCommon.wmPoiName,
        customizedShowArr: state.shellCommon.customizedShowArr,
        poilistAjaxType: state.shellCommon.poilistAjaxType,
    };
};

export default connect(mapStateToProps)(PoiDropMenu);
